import React, { useState, useRef, useEffect } from "react";
import Cropper from "react-easy-crop";
import { SketchPicker } from "react-color";
import bg from "./background.jpg";

// --- Anonymous user (stable per browser) ---
const getUserId = () => {
  try {
    let id = localStorage.getItem("wv_user_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("wv_user_id", id);
    }
    return id;
  } catch {
    return "anon";
  }
};

export default function App() {
  // === Core images & compare
  const [roomImage, setRoomImage] = useState(null);
  const [roomImageUrl, setRoomImageUrl] = useState(null);
  const [resultImage, setResultImage] = useState(null);
  const [compareLeftImage, setCompareLeftImage] = useState(null);
  const [compareRightImage, setCompareRightImage] = useState(null);

  const [compareMode, setCompareMode] = useState(false); // OFF by default
  const [sliderValue, setSliderValue] = useState(50);

  // === Wallpaper / color options
  const [wallpaper, setWallpaper] = useState(null);
  const [wallpaperMode, setWallpaperMode] = useState("stretch");
  const [tileSize, setTileSize] = useState("medium");
  const [color, setColor] = useState("#ffffff");
  const [showColorPicker, setShowColorPicker] = useState(false);

  // === Points & modes
  const [initialPoints, setInitialPoints] = useState([]);
  const [correctionPoints, setCorrectionPoints] = useState([]);
  const [paintMode, setPaintMode] = useState("paint"); // "paint" or "erase"

  // === Crop
  const [showCrop, setShowCrop] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // === refs & sizes
  const uploadImgRef = useRef(null);
  const resultImgRef = useRef(null);
  const [uploadNatural, setUploadNatural] = useState({ w: 0, h: 0 });
  const [uploadRendered, setUploadRendered] = useState({ w: 0, h: 0 });
  const [resultNatural, setResultNatural] = useState({ w: 0, h: 0 });
  const [resultRendered, setResultRendered] = useState({ w: 0, h: 0 });

  // dragging state
  const [draggingIndex, setDraggingIndex] = useState(null);
  const dragTargetRef = useRef(null); // "initial" or "correction"
  const lastPointerId = useRef(null);

  // UI
  const [isProcessing, setIsProcessing] = useState(false);

  //credits
  const [credits, setCredits] = useState(null); // null = not yet known

  //backend URL
  const BACKEND_URL =
    "https://wall-visualizer-backend-production.up.railway.app";

  /*warmup backend (non-blocking)
  useEffect(() => {
    const BACKEND_URL =
      "https://wall-visualizer-backend-production.up.railway.app";
    fetch(`${BACKEND_URL}/warmup`).catch(() => {});
  }, []); */

  //----load credits from backend----
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/credits`, {
          headers: { "X-User-ID": getUserId() },
        });
        const data = await r.json();
        setCredits(typeof data.credits === "number" ? data.credits : 0);
      } catch (e) {
        console.error("Failed to fetch credits", e);
        setCredits(0);
      }
    };

    fetchCredits();
  }, []);

  //loadrazorpay
  const loadRazorpay = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;

      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Razorpay SDK failed to load"));

      document.body.appendChild(script);
    });

  // create room image URL lifecycle
  useEffect(() => {
    if (!roomImage) {
      if (roomImageUrl) URL.revokeObjectURL(roomImageUrl);
      setRoomImageUrl(null);
      return;
    }
    const u = URL.createObjectURL(roomImage);
    setRoomImageUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [roomImage]);

  // ---- helpers to map client coords -> natural coords ----
  const mapClientToNaturalUsingRect = (clientX, clientY, rect, natural) => {
    if (!rect || !natural) return { x: 0, y: 0 };
    const xRendered = clientX - rect.left;
    const yRendered = clientY - rect.top;
    const wRendered = rect.width || 1;
    const hRendered = rect.height || 1;
    const nx = Math.round((xRendered / wRendered) * (natural.w || wRendered));
    const ny = Math.round((yRendered / hRendered) * (natural.h || hRendered));
    return { x: nx, y: ny };
  };

  // ----- helper for Razorpay (DEBUG + backend-aligned)
  const buyCredits = async (creditsToBuy = 5) => {
    try {
      await loadRazorpay();
      const userId = getUserId();

      console.log("Creating Razorpay order:", {
        backend: `${BACKEND_URL}/create_order`,
        userId,
        credits: creditsToBuy,
      });

      const orderRes = await fetch(`${BACKEND_URL}/create_order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-ID": userId,
        },
        body: JSON.stringify({
          credits: Number(creditsToBuy),
        }),
      });

      if (!orderRes.ok) {
        const text = await orderRes.text();
        console.error("create_order failed:", orderRes.status, text);
        alert(`Order creation failed (${orderRes.status})`);
        return;
      }

      const order = await orderRes.json();

      const options = {
        key: "rzp_live_Rs9VzbrRb4Czgc",
        amount: order.amount,
        currency: "INR",
        order_id: order.id,
        handler: async (resp) => {
          const verifyRes = await fetch(`${BACKEND_URL}/verify_payment`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-User-ID": userId, // important for DB update
            },
            body: JSON.stringify({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              credits: creditsToBuy, // tell backend how many credits to add
            }),
          });

          if (!verifyRes.ok) {
            alert("Payment verification failed");
            return;
          }

          const c = await fetch(`${BACKEND_URL}/credits`, {
            headers: { "X-User-ID": userId },
          }).then((r) => r.json());

          setCredits(c.credits === 0 ? 2 : c.credits);
          alert("Credits added successfully!");
        },
        theme: { color: "#ff6600" },
      };

      new window.Razorpay(options).open();
    } catch (e) {
      console.error("buyCredits fatal error:", e);
      alert("Unable to start payment. Check console.");
    }
  };

  // convenience wrapper for image refs (normal result)
  const clientToNaturalForResult = (clientX, clientY) => {
    if (!resultImgRef.current) return { x: 0, y: 0 };
    const rect = resultImgRef.current.getBoundingClientRect();
    return mapClientToNaturalUsingRect(clientX, clientY, rect, resultNatural);
  };

  const clientToNaturalForUpload = (clientX, clientY) => {
    if (!uploadImgRef.current) return { x: 0, y: 0 };
    const rect = uploadImgRef.current.getBoundingClientRect();
    return mapClientToNaturalUsingRect(clientX, clientY, rect, uploadNatural);
  };

  // ---- upload click: add/remove initial points ----
  const handleUploadClick = (e) => {
    if (!uploadImgRef.current) return;
    const { x, y } = clientToNaturalForUpload(e.clientX, e.clientY);
    const proximity = 15;
    const existing = initialPoints.findIndex(
      (p) => Math.hypot(p.x - x, p.y - y) < proximity
    );
    if (existing !== -1) {
      // remove if close
      setInitialPoints((prev) => prev.filter((_, i) => i !== existing));
    } else {
      setInitialPoints((prev) => [...prev, { x, y, mode: "paint" }]);
    }
  };

  // ---- result / compare frame click: add/remove correction points ----
  const handleCompareOrResultClick = (e) => {
    // if compareMode and compare frame exists -> map using that frame's rect
    const compareFrame = document.getElementById("compareFrame");
    if (compareMode && compareFrame) {
      const rect = compareFrame.getBoundingClientRect();
      // map to natural coords of result (compare images resultNatural)
      const { x, y } = mapClientToNaturalUsingRect(
        e.clientX,
        e.clientY,
        rect,
        resultNatural
      );
      const proximity = 15;
      const existing = correctionPoints.findIndex(
        (p) => Math.hypot(p.x - x, p.y - y) < proximity
      );
      if (existing !== -1) {
        setCorrectionPoints((prev) => prev.filter((_, i) => i !== existing));
      } else {
        setCorrectionPoints((prev) => [...prev, { x, y, mode: paintMode }]);
      }
      return;
    }

    // else normal result image
    if (resultImgRef.current) {
      const { x, y } = clientToNaturalForResult(e.clientX, e.clientY);
      const proximity = 15;
      const existing = correctionPoints.findIndex(
        (p) => Math.hypot(p.x - x, p.y - y) < proximity
      );
      if (existing !== -1) {
        setCorrectionPoints((prev) => prev.filter((_, i) => i !== existing));
      } else {
        setCorrectionPoints((prev) => [...prev, { x, y, mode: paintMode }]);
      }
    }
  };

  // ---- pointer drag handlers for dots (works for initial & correction) ----
  const onDotPointerDown = (e, idx, which) => {
    e.stopPropagation();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {}
    lastPointerId.current = e.pointerId;
    setDraggingIndex(idx);
    dragTargetRef.current = which; // "initial" or "correction"
  };

  const onDotPointerMove = (e) => {
    if (draggingIndex === null || e.pointerId !== lastPointerId.current) return;
    const { clientX, clientY } = e;
    if (dragTargetRef.current === "initial") {
      const { x, y } = clientToNaturalForUpload(clientX, clientY);
      setInitialPoints((prev) =>
        prev.map((p, i) => (i === draggingIndex ? { ...p, x, y } : p))
      );
    } else {
      // use compare frame rect if present to get accurate rendered size
      const compareFrame = document.getElementById("compareFrame");
      if (compareMode && compareFrame) {
        const rect = compareFrame.getBoundingClientRect();
        const { x, y } = mapClientToNaturalUsingRect(
          clientX,
          clientY,
          rect,
          resultNatural
        );
        setCorrectionPoints((prev) =>
          prev.map((p, i) => (i === draggingIndex ? { ...p, x, y } : p))
        );
      } else {
        const { x, y } = clientToNaturalForResult(clientX, clientY);
        setCorrectionPoints((prev) =>
          prev.map((p, i) => (i === draggingIndex ? { ...p, x, y } : p))
        );
      }
    }
  };

  const onDotPointerUp = (e) => {
    if (draggingIndex === null) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {}
    setDraggingIndex(null);
    dragTargetRef.current = null;
    lastPointerId.current = null;
  };

  // ---- crop helpers (free aspect) ----
  const onFilePicked = (file) => {
    if (!file) return;
    setRoomImage(file);
    setResultImage(null);
    setCompareLeftImage(null);
    setCompareRightImage(null);
    setInitialPoints([]);
    setCorrectionPoints([]);
    setShowCrop(true);
  };

  const onCropComplete = (_, areaPixels) => setCroppedAreaPixels(areaPixels);

  const applyCrop = async () => {
    if (!croppedAreaPixels || !roomImageUrl) {
      setShowCrop(false);
      return;
    }
    const img = new Image();
    img.src = roomImageUrl;
    await new Promise((r) => (img.onload = r));
    const canvas = document.createElement("canvas");
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      img,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );
    const blob = await new Promise((res) =>
      canvas.toBlob(res, "image/jpeg", 0.95)
    );
    const file = new File([blob], "cropped.jpg", { type: "image/jpeg" });
    setRoomImage(file);
    setShowCrop(false);
  };

  const cancelCrop = () => setShowCrop(false);

  // ---- onLoad size trackers ----
  const onUploadLoad = (e) => {
    const img = e.target;
    setUploadNatural({ w: img.naturalWidth, h: img.naturalHeight });
    const rect = img.getBoundingClientRect();
    setUploadRendered({ w: rect.width, h: rect.height });
  };
  const onResultLoad = (e) => {
    const img = e.target;
    setResultNatural({ w: img.naturalWidth, h: img.naturalHeight });
    const rect = img.getBoundingClientRect();
    setResultRendered({ w: rect.width, h: rect.height });
  };

  // ---- submit to backend ----
  const handleSubmit = async (auto = false) => {
    if (!roomImage) {
      if (!auto) alert("Upload a room image first");
      return;
    }

    if (initialPoints.length < 4) {
      if (!auto) alert("Select at least 4–6 wall points");
      return;
    }

    setIsProcessing(true);

    const combined = [...initialPoints, ...correctionPoints];
    const fd = new FormData();
    fd.append("image", roomImage);
    fd.append("points", JSON.stringify(combined));
    if (color) fd.append("color", color);
    if (wallpaper) fd.append("wallpaper", wallpaper);
    fd.append("wallpaperMode", wallpaperMode);
    fd.append("tileSize", tileSize);

    try {
      const r = await fetch(`${BACKEND_URL}/segment`, {
        method: "POST",
        headers: { "X-User-ID": getUserId() },
        body: fd,
      });

      if (!r.ok) {
        if (r.status === 402) {
          alert("You are out of credits. Please buy more to continue.");
          setCredits(0);
          return;
        }
        const text = await r.text();
        throw new Error(text);
      }

      // ---- read image ----
      const blob = await r.blob();
      const newUrl = URL.createObjectURL(blob);

      // ---- decrement credit ONCE ----
      setCredits((c) => Math.max(0, c - 1));

      // ---- Compare / normal logic (UNCHANGED) ----
      if (compareMode) {
        if (!compareLeftImage) {
          if (compareLeftImage) URL.revokeObjectURL(compareLeftImage);
          setCompareLeftImage(newUrl);
          setResultImage(newUrl);
          setRoomImageUrl(null);
        } else {
          if (compareRightImage) URL.revokeObjectURL(compareRightImage);
          setCompareRightImage(newUrl);
          setResultImage(newUrl);
          setRoomImageUrl(null);
        }
      } else {
        if (resultImage) URL.revokeObjectURL(resultImage);
        if (compareLeftImage) {
          URL.revokeObjectURL(compareLeftImage);
          setCompareLeftImage(null);
        }
        if (compareRightImage) {
          URL.revokeObjectURL(compareRightImage);
          setCompareRightImage(null);
        }
        setResultImage(newUrl);
        setRoomImageUrl(null);
      }
    } catch (err) {
      console.error(err);
      if (!auto) alert("Something went wrong. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  // ---- reset / download / helpers
  const resetAll = () => {
    if (resultImage) URL.revokeObjectURL(resultImage);
    if (compareLeftImage) URL.revokeObjectURL(compareLeftImage);
    if (compareRightImage) URL.revokeObjectURL(compareRightImage);

    setRoomImage(null);
    setRoomImageUrl(null);
    setResultImage(null);
    setCompareLeftImage(null);
    setCompareRightImage(null);
    setWallpaper(null);
    setInitialPoints([]);
    setCorrectionPoints([]);
    setPaintMode("paint");
    setSliderValue(50);
    setCompareMode(false);
  };

  const handleDownload = () => {
    // prefer right image if comparing and present else result/left
    const target =
      compareMode && compareRightImage
        ? compareRightImage
        : resultImage || compareLeftImage;
    if (!target) return;
    const a = document.createElement("a");
    a.href = target;
    a.download = "wall-visualizer-result.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const clearCompare = () => {
    if (compareLeftImage) URL.revokeObjectURL(compareLeftImage);
    if (compareRightImage) URL.revokeObjectURL(compareRightImage);
    setCompareLeftImage(null);
    setCompareRightImage(null);
    setSliderValue(50);
    setResultImage(null);
    // restore upload preview for further interaction
    if (roomImage) {
      const url = URL.createObjectURL(roomImage);
      setRoomImageUrl(url);
    }
  };

  // slider drag
  const handleSliderPointerDown = (e) => {
    e.preventDefault();
    const moveListener = (ev) => {
      const rect = document
        .querySelector("#compareFrame")
        .getBoundingClientRect();
      const x =
        ev.clientX ?? (ev.touches && ev.touches[0] && ev.touches[0].clientX);
      if (x == null) return;
      const pct = ((x - rect.left) / rect.width) * 100;
      setSliderValue(Math.max(0, Math.min(100, pct)));
    };
    const upListener = () => {
      window.removeEventListener("pointermove", moveListener);
      window.removeEventListener("pointerup", upListener);
      window.removeEventListener("touchmove", moveListener);
      window.removeEventListener("touchend", upListener);
    };
    window.addEventListener("pointermove", moveListener);
    window.addEventListener("pointerup", upListener);
    window.addEventListener("touchmove", moveListener);
    window.addEventListener("touchend", upListener);
  };

  // styles
  const orangeBtn = {
    padding: "8px 14px",
    borderRadius: "18px",
    background: "#ff6600",
    color: "white",
    border: "none",
    cursor: "pointer",
    fontWeight: "bold",
    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
    fontSize: "clamp(12px, 2.5vw, 16px)",
  };
  const selectStyle = { margin: 5, padding: "6px 10px", borderRadius: "10px" };

  // ----- RENDER -----
  return (
    <div
      style={{
        textAlign: "center",
        padding: "20px",
        minHeight: "100vh",
        fontFamily: "'Playfair Display', serif",
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
      }}
    >
      <h2 style={{ color: "#ff6600" }}>Wall & Beyond</h2>
      <h3 style={{ color: "#ff6600" }}> Your Wall Visualizer</h3>

      {/* Upload controls */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 8,
        }}
      >
        <label style={orangeBtn}>
          Upload Room
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onFilePicked(e.target.files[0])}
            style={{ display: "none" }}
          />
        </label>
        <label style={orangeBtn}>
          Upload Wallpaper
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setWallpaper(e.target.files[0])}
            style={{ display: "none" }}
          />
        </label>
        {wallpaper && (
          <button
            style={{ ...orangeBtn, background: "#ff4444" }}
            onClick={() => setWallpaper(null)}
          >
            ✖ Remove Wallpaper
          </button>
        )}
      </div>

      {/* Wallpaper options & Compare toggle */}
      {wallpaper && (
        <div style={{ marginTop: 8 }}>
          <select
            value={wallpaperMode}
            onChange={(e) => setWallpaperMode(e.target.value)}
            style={selectStyle}
          >
            <option value="stretch">Stretch</option>
            <option value="tile">Tile</option>
          </select>
          <p>
            To compare another wallpaper,click compare mode on and upload
            wallpaper using upload-wallpaper button
          </p>
          {wallpaperMode === "tile" && (
            <select
              value={tileSize}
              onChange={(e) => setTileSize(e.target.value)}
              style={selectStyle}
            >
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
          )}

          <label
            style={{
              marginLeft: 12,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <input
              type="checkbox"
              checked={compareMode}
              onChange={() => {
                const nv = !compareMode;
                setCompareMode(nv);
                if (!nv) {
                  // clear compare images when turning off
                  if (compareLeftImage) URL.revokeObjectURL(compareLeftImage);
                  if (compareRightImage) URL.revokeObjectURL(compareRightImage);
                  setCompareLeftImage(null);
                  setCompareRightImage(null);
                  setSliderValue(50);
                } else {
                  // turning on - preserve current result as left if present
                  if (resultImage && !compareLeftImage)
                    setCompareLeftImage(resultImage);
                }
              }}
            />
            <span style={{ fontSize: 14 }}>Compare Mode</span>
          </label>
        </div>
      )}

      {/* Color Picker */}
      <div style={{ marginTop: 10 }}>
        <button style={orangeBtn} onClick={() => setShowColorPicker((s) => !s)}>
          {showColorPicker ? "Close Color Picker" : "Pick Paint Color"}
        </button>
        {showColorPicker && (
          <div style={{ marginTop: 8, display: "inline-block" }}>
            <SketchPicker
              color={color}
              onChange={(c) => setColor(c.hex)}
              disableAlpha
            />
          </div>
        )}
      </div>

      {/* Crop UI */}
      {showCrop && roomImageUrl && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              width: "90vw",
              maxWidth: 900,
              background: "white",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <div
              style={{ position: "relative", width: "100%", height: "60vh" }}
            >
              <Cropper
                image={roomImageUrl}
                crop={crop}
                zoom={zoom}
                aspect={undefined}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div
              style={{
                marginTop: 8,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                style={{ ...orangeBtn, background: "#888" }}
                onClick={cancelCrop}
              >
                Cancel
              </button>
              <button
                style={{ ...orangeBtn, background: "#28a745" }}
                onClick={applyCrop}
              >
                Use Crop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spinner overlay */}
      {isProcessing && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            color: "white",
            fontSize: 20,
            flexDirection: "column",
          }}
        >
          <div
            className="spinner"
            style={{
              border: "4px solid #fff",
              borderTop: "4px solid #ff6600",
              borderRadius: "50%",
              width: 40,
              height: 40,
              animation: "spin 1s linear infinite",
            }}
          />
          <p style={{ marginTop: 10 }}>Applying changes...</p>
          <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
        </div>
      )}

      {/* Upload stage */}
      {roomImageUrl &&
        !resultImage &&
        !compareLeftImage &&
        !compareRightImage && (
          <div style={{ marginTop: 12 }}>
            <p>Tap to add/move/remove dots. Drag to adjust.</p>
            <p>
              Hint: Click 4–6 points where you want the wallpaper to be applied.
            </p>
            <p
              style={{
                margin: "6px 0",
                fontSize: 13,
                color: "#555",
              }}
            >
              💡 This visualizer works for <strong>walls</strong> as well as{" "}
              <strong>floors / tiles</strong>. Use it to preview tiles, marble,
              or stone layouts too.
            </p>

            <div
              style={{ position: "relative", display: "inline-block" }}
              onClick={handleUploadClick}
            >
              <img
                ref={uploadImgRef}
                src={roomImageUrl}
                alt="room"
                style={{ maxWidth: "70vw" }}
                onLoad={onUploadLoad}
                draggable={false}
              />
              {initialPoints.map((p, i) => {
                const left = (p.x / (uploadNatural.w || 1)) * 100;
                const top = (p.y / (uploadNatural.h || 1)) * 100;
                return (
                  <div
                    key={i}
                    onPointerDown={(e) => onDotPointerDown(e, i, "initial")}
                    onPointerMove={onDotPointerMove}
                    onPointerUp={onDotPointerUp}
                    style={{
                      position: "absolute",
                      left: `${left}%`,
                      top: `${top}%`,
                      transform: "translate(-50%,-50%)",
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: "rgba(255,0,0,0.95)",
                      border: "2px solid white",
                      cursor: "grab",
                    }}
                  />
                );
              })}
            </div>
            <div style={{ marginTop: 10 }}>
              <button style={orangeBtn} onClick={() => setShowCrop(true)}>
                Crop Image
              </button>
              <button
                style={orangeBtn}
                onClick={() => handleSubmit(false)}
                disabled={isProcessing}
              >
                Apply
              </button>
              {credits !== null && (
                <div style={{ marginBottom: 6, fontSize: 14, color: "#333" }}>
                  Credits remaining: <strong>{credits}</strong>
                </div>
              )}

              {credits === 0 && (
                <button
                  style={{ ...orangeBtn, background: "#444" }}
                  onClick={() => buyCredits(5)}
                >
                  Buy Credits
                </button>
              )}

              <button style={orangeBtn} onClick={resetAll}>
                Reset
              </button>
            </div>
          </div>
        )}

      {/* Compare frame */}
      {compareMode && compareLeftImage && (
        <div
          id="compareContainer"
          style={{
            marginTop: 16,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <p style={{ marginBottom: 8 }}>
            Compare Mode — drag to reveal. First Apply = left, second Apply =
            right.
          </p>

          <div
            id="compareFrame"
            style={{
              width: "70vw",
              maxWidth: 900,
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
              position: "relative",
              userSelect: "none",
              touchAction: "none",
              background: "#fff",
            }}
            onClick={handleCompareOrResultClick}
          >
            {/* base left */}
            <img
              src={compareLeftImage}
              alt="left"
              style={{
                width: "100%",
                display: "block",
                filter: "contrast(1.02)",
              }}
            />

            {/* right image clipped */}
            {compareRightImage && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  overflow: "hidden",
                  pointerEvents: "none",
                }}
              >
                <img
                  src={compareRightImage}
                  alt="right"
                  style={{
                    width: "100%",
                    position: "absolute",
                    left: 0,
                    top: 0,
                    clipPath: `inset(0 ${100 - sliderValue}% 0 0)`,
                    transition: "clip-path 120ms linear",
                  }}
                />
              </div>
            )}

            {/* correction dots overlay (interactive) */}
            {resultNatural.w > 0 &&
              correctionPoints.map((p, i) => {
                const left = (p.x / (resultNatural.w || 1)) * 100;
                const top = (p.y / (resultNatural.h || 1)) * 100;
                return (
                  <div
                    key={"c" + i}
                    onPointerDown={(e) => onDotPointerDown(e, i, "correction")}
                    onPointerMove={onDotPointerMove}
                    onPointerUp={onDotPointerUp}
                    style={{
                      position: "absolute",
                      left: `${left}%`,
                      top: `${top}%`,
                      transform: "translate(-50%,-50%)",
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: p.mode === "erase" ? "red" : "green",
                      border: "2px solid white",
                      cursor: "grab",
                      zIndex: 20,
                      pointerEvents: "auto",
                    }}
                  />
                );
              })}

            {/* invisible capture layer ensures clicks on image are handled (but not blocking handle) */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "auto",
                zIndex: 10,
              }}
            />

            {/* slider handle */}
            <div
              role="slider"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(sliderValue)}
              onPointerDown={handleSliderPointerDown}
              onTouchStart={handleSliderPointerDown}
              style={{
                position: "absolute",
                left: `${sliderValue}%`,
                top: 0,
                bottom: 0,
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "auto",
                zIndex: 30,
              }}
            >
              <div
                style={{
                  width: 2,
                  height: "100%",
                  background: "rgba(255,255,255,0.6)",
                  boxShadow: "0 0 0 1px rgba(0,0,0,0.06) inset",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  transform: "translate(-50%,-50%)",
                  left: "50%",
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
                  border: "3px solid rgba(255,102,0,0.95)",
                  pointerEvents: "auto",
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    background: "#ff6600",
                  }}
                />
              </div>
            </div>

            {/* labels */}
            <div
              style={{
                position: "absolute",
                left: 12,
                top: 12,
                background: "rgba(255,255,255,0.85)",
                padding: "6px 10px",
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              Before
            </div>
            <div
              style={{
                position: "absolute",
                right: 12,
                top: 12,
                background: "rgba(255,255,255,0.85)",
                padding: "6px 10px",
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              After
            </div>
          </div>

          {/* compare controls with Add/Remove present */}
          <div
            style={{
              marginTop: 12,
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <button
              style={{
                ...orangeBtn,
                background:
                  paintMode === "paint" ? "#ff6600" : "rgba(255,102,0,0.4)",
              }}
              onClick={() => setPaintMode("paint")}
            >
              Add
            </button>
            <button
              style={{
                ...orangeBtn,
                background:
                  paintMode === "erase" ? "#ff6600" : "rgba(255,102,0,0.4)",
              }}
              onClick={() => setPaintMode("erase")}
            >
              Remove
            </button>

            {/* Apply always available */}
            <button
              style={orangeBtn}
              onClick={() => handleSubmit(false)}
              disabled={isProcessing}
            >
              Apply
            </button>

            <button
              style={{ ...orangeBtn }}
              onClick={() => {
                if (!compareRightImage) return;
                const L = compareLeftImage;
                const R = compareRightImage;
                setCompareLeftImage(R);
                setCompareRightImage(L);
                setResultImage(R);
                setSliderValue(50);
              }}
              disabled={!compareRightImage}
            >
              Swap Sides
            </button>

            <button
              style={orangeBtn}
              onClick={() => {
                clearCompare();
                setCompareMode(false);
              }}
            >
              Clear Compare
            </button>

            <input
              type="range"
              min="0"
              max="100"
              value={sliderValue}
              onChange={(e) => setSliderValue(Number(e.target.value))}
              style={{ width: 180 }}
            />

            <button style={orangeBtn} onClick={handleDownload}>
              Download Result
            </button>
            <button style={orangeBtn} onClick={resetAll}>
              Reset All
            </button>
          </div>
        </div>
      )}

      {/* Single result view (when not compare mode or no compare images) */}
      {!compareMode && resultImage && (
        <div style={{ marginTop: 16 }}>
          <p>Click to add/remove, drag to adjust.</p>
          <div
            style={{ position: "relative", display: "inline-block" }}
            onClick={handleCompareOrResultClick}
          >
            <img
              ref={resultImgRef}
              src={resultImage}
              alt="result"
              style={{
                maxWidth: "70vw",
                borderRadius: 12,
                boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
              }}
              onLoad={onResultLoad}
              draggable={false}
            />
            {correctionPoints.map((p, i) => {
              const left = (p.x / (resultNatural.w || 1)) * 100;
              const top = (p.y / (resultNatural.h || 1)) * 100;
              return (
                <div
                  key={i}
                  onPointerDown={(e) => onDotPointerDown(e, i, "correction")}
                  onPointerMove={onDotPointerMove}
                  onPointerUp={onDotPointerUp}
                  style={{
                    position: "absolute",
                    left: `${left}%`,
                    top: `${top}%`,
                    transform: "translate(-50%,-50%)",
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    background: p.mode === "erase" ? "red" : "green",
                    border: "2px solid white",
                    cursor: "grab",
                  }}
                />
              );
            })}
          </div>

          <div style={{ marginTop: 10 }}>
            <button
              style={{
                ...orangeBtn,
                background:
                  paintMode === "paint" ? "#ff6600" : "rgba(255,102,0,0.4)",
              }}
              onClick={() => setPaintMode("paint")}
            >
              Add
            </button>
            <button
              style={{
                ...orangeBtn,
                background:
                  paintMode === "erase" ? "#ff6600" : "rgba(255,102,0,0.4)",
              }}
              onClick={() => setPaintMode("erase")}
            >
              Remove
            </button>
            <button style={orangeBtn} onClick={() => handleSubmit(false)}>
              Apply
            </button>
            <button style={orangeBtn} onClick={handleDownload}>
              Download
            </button>
            <button style={orangeBtn} onClick={resetAll}>
              Reset All
            </button>
          </div>
        </div>
      )}

      {/* fallback: when compare is off but left exists */}
      {!compareMode && !resultImage && compareLeftImage && (
        <div style={{ marginTop: 16 }}>
          <img
            src={compareLeftImage}
            alt="left"
            style={{
              maxWidth: "70vw",
              borderRadius: 12,
              boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            }}
            onLoad={onResultLoad}
            draggable={false}
          />
          <div style={{ marginTop: 10 }}>
            <button style={orangeBtn} onClick={() => handleSubmit(false)}>
              Apply
            </button>

            <button style={orangeBtn} onClick={handleDownload}>
              Download
            </button>
            <button style={orangeBtn} onClick={resetAll}>
              Reset All
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
